import { useAuth } from '@teko/auth-react';
import { DiscountBenefit, Gift, VoucherPromotion } from '@teko/ecommerce';
import { logger } from '@teko/log';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useFeatureFlags, useTenantConfig } from '../contexts';
import { checkExpiryDate } from '../helpers';
import { getProductBundleOrCombo, ppmService } from '../services';
import {
  BENEFIT_TYPE,
  ConvertedProduct,
  EComboBundleSourceType,
  ProductDetailPromotion,
  ProductDetailPromotionV2,
  PromotionResponse,
} from '../types';

const transformPromotionV2 = (
  promotion: PromotionResponse,
  productSku: string,
): ProductDetailPromotionV2[] => {
  const { condition } = promotion;
  const basePromotion = {
    ...promotion,
    condition: {
      ...condition,
      hasProductCondition: !!condition.skus.filter(
        ({ sku }) => sku === productSku,
      ).length,
    },
  };

  return [basePromotion];
};
// Flat benefit
const transformPromotion = (
  promotion: PromotionResponse,
  productSku: string,
): ProductDetailPromotion[] => {
  const { benefit, condition } = promotion;
  const { gifts, voucher, discount } = benefit;

  const basePromotion = {
    ...promotion,
    condition: {
      ...condition,
      hasProductCondition: !!condition.skus.filter(
        ({ sku }) => sku === productSku,
      ).length,
    },
  };

  const giftData =
    gifts && gifts.length
      ? [
          {
            ...basePromotion,
            benefit: {
              type: BENEFIT_TYPE.gifts,
              data: gifts as Gift[],
            },
          },
        ]
      : [];

  const voucherData =
    voucher && Object.keys(voucher).length
      ? [
          {
            ...basePromotion,
            benefit: {
              type: BENEFIT_TYPE.voucher,
              data: voucher as VoucherPromotion,
            },
          },
        ]
      : [];

  const discountData =
    discount && Object.keys(discount).length
      ? [
          {
            ...basePromotion,
            benefit: {
              type: BENEFIT_TYPE.discount,
              data: discount as DiscountBenefit,
            },
          },
        ]
      : [];

  return [...discountData, ...giftData, ...voucherData];
};

/** Valid promotions:
 * public
 * applied for product sku or all skus
 * applied for sellerId of product or all sellers
 * not expired
 */
const filterValidPromotions = (
  promotions: PromotionResponse[],
  { seller, sku: productSku }: ConvertedProduct,
): PromotionResponse[] => {
  return promotions.filter(
    ({ applySellerIds, startedAt, endedAt, condition }) => {
      const isValidSku =
        !condition.skus.length ||
        condition.skus.filter(({ sku }) => sku === productSku).length;

      const isValidSellerId =
        !applySellerIds.length ||
        (seller && applySellerIds.includes(seller.id));

      const isValidDate =
        !!startedAt &&
        !!endedAt &&
        checkExpiryDate(
          new Date(startedAt).toISOString(),
          new Date(endedAt).toISOString(),
        );
      return isValidSku && isValidSellerId && isValidDate;
    },
  );
};

const getShowedPromotions = (
  promotions: ProductDetailPromotion[],
  price: number,
) => {
  return promotions.filter(({ condition }) => {
    if (!condition) return false;
    const { hasProductCondition, orderValueMin } = condition;

    return (
      hasProductCondition ||
      (!hasProductCondition &&
        (orderValueMin === null ||
          orderValueMin === undefined ||
          price >= orderValueMin))
    );
  });
};
const isBenefitEmpty = (benefit: ProductDetailPromotionV2['benefit']) => {
  return (
    (!benefit.gifts || !benefit.gifts.length) &&
    (!benefit.money || !benefit.money.length) &&
    (!benefit.items || !benefit.items.length) &&
    !benefit.voucher &&
    !benefit.discount
  );
};
const getShowedPromotionsV2 = (
  promotions: ProductDetailPromotionV2[],
  price: number,
) => {
  return promotions.filter(({ condition, isDefault, benefit }) => {
    if (!condition) return false;
    // Check valid promotion according to task https://jira.teko.vn/browse/UX4BUYER-7825
    if (!isDefault && !condition.coupon && isBenefitEmpty(benefit))
      return false;

    const { hasProductCondition, orderValueMin } = condition;

    return (
      hasProductCondition ||
      (!hasProductCondition &&
        (orderValueMin === null ||
          orderValueMin === undefined ||
          price >= orderValueMin))
    );
  });
};
export const useDetailPromotion = (
  product?: ConvertedProduct,
  price?: number,
) => {
  const [allPromotions, setAllPromotions] = useState<ProductDetailPromotion[]>(
    [],
  );
  const [validPromotions, setValidPromotions] = useState<
    ProductDetailPromotionV2[]
  >([]);
  const [promotions, setPromotions] = useState<ProductDetailPromotion[]>([]);

  const { user, loading } = useAuth();

  const {
    currentLanguage,
    terminalId,
    terminal,
    useTerminalId,
    displayPublicCouponInRelatedPromotions,
  } = useTenantConfig();

  const { flags } = useFeatureFlags();

  useEffect(() => {
    let isSubscribed = true;
    if (product && !loading) {
      const { sku } = product;
      if (!sku) {
        logger.error(
          `Exception: Calling PPM API get promotions with no product SKU, product info: ${JSON.stringify(
            product || {},
          )}`,
        );
        return;
      }
      const getPromotionPromise = user
        ? ppmService.getPrivatePromotion
        : ppmService.getRelatedPromotion;
      getPromotionPromise({
        type: displayPublicCouponInRelatedPromotions
          ? 'coupon,order,qr,gift-code'
          : 'coupon,order,qr',
        sku: sku,
        ...(flags.epic_ecom_284 || useTerminalId
          ? { terminalId }
          : { terminal }),
        language: currentLanguage,
      }).then((promotions: PromotionResponse[]) => {
        if (!isSubscribed) return;
        const validPromotions = filterValidPromotions(promotions, product);

        const formattedPromotions: ProductDetailPromotion[] = [];
        const formattedPromotionsV2: ProductDetailPromotionV2[] = [];
        for (const index in validPromotions) {
          //Epic: https://jira.teko.vn/browse/OMNI-552
          if (validPromotions[index].type === 'qr') {
            formattedPromotions.push({
              ...(validPromotions[index] as unknown as ProductDetailPromotion),
            });
            formattedPromotionsV2.push({
              ...(validPromotions[
                index
              ] as unknown as ProductDetailPromotionV2),
            });
          } else {
            formattedPromotions.push(
              ...transformPromotion(validPromotions[index], sku),
            );
            if (
              !validPromotions[index].isDefault &&
              !validPromotions[index].condition?.coupon &&
              isBenefitEmpty(validPromotions[index].benefit)
            ) {
              continue;
            }
            formattedPromotionsV2.push(
              ...transformPromotionV2(validPromotions[index], sku),
            );
          }
        }
        setAllPromotions(formattedPromotions);
        setValidPromotions(formattedPromotionsV2);
      });
    }
    return () => {
      isSubscribed = false;
    };
  }, [user, loading]);

  useEffect(() => {
    if (price && allPromotions.length) {
      setPromotions(getShowedPromotions(allPromotions, price));
      setValidPromotions(getShowedPromotionsV2(validPromotions, price));
    }
  }, [price, JSON.stringify(allPromotions)]);

  return {
    promotions,
    validPromotions,
  };
};

export const useGetProductsCombos = (
  skus: string[] = [],
  hasCompulsoryProducts: boolean = false,
) => {
  const {
    currentLanguage: language,
    terminalId,
    epicOmni1286,
    epicOmni1790,
    platformId,
  } = useTenantConfig();
  const sourceType = hasCompulsoryProducts
    ? EComboBundleSourceType.BUNDLE_RULES
    : EComboBundleSourceType.COMBO_PROMOTIONS;
  // TODO: deprecate API core entirely when OMNI-1790 stable
  const shouldCallBff = epicOmni1790 && hasCompulsoryProducts;

  const { data, error } = useSWR(
    epicOmni1286 && !!skus.length ? ['combos', skus.join()] : null, // not call API when epicOmni1286 = false or no skus
    async (_) => {
      if (shouldCallBff) {
        if (!platformId) return;
        return getProductBundleOrCombo(
          {
            platformId,
            sourceType,
            skus: skus.join(','),
            terminalId,
          },
          language,
        );
      }
      return ppmService.getProductsCombos({ skus, terminalId, language });
    },
    {
      revalidateOnFocus: false,
    },
  );

  const products =
    (shouldCallBff
      ? (data as Awaited<ReturnType<typeof getProductBundleOrCombo>>)?.data
          ?.products
      : (data as Awaited<ReturnType<typeof ppmService.getProductsCombos>>)
          ?.result?.products) || [];

  return {
    result: { products },
    isLoading: !error && !data,
    isError: error,
  };
};
